import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import AdminPageLayout from "../../../../components/FullPageLayout/AdminPageLayout";
import { withRouter } from "react-router";
import { push } from "connected-react-router";
import { useDispatch, useSelector } from "react-redux";
import { Route, Switch, matchPath } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import Tabs from "../../../../components/Tabs/Tabs";
import {
  root,
  courses,
  buildCourseDetailsTabRoute,
  buildScheduleSessionRoute,
  courseDetails
} from "../../../../routes/AdminRoutes";
import Breadcrumb from "../../../../components/Breadcrumb/Breadcrumb";
import Instructors from "./Instructors";
import VirtualClassroomsTable from "./VirtualClassroomsTable";
import TrainingLocations from "./TrainingLocations";
import usePermissions from "../../../../redux/selectors/usePermissions";

const selector = makeGetEntities();

const ManageInstructorsLocations = ({
  match: {
    url: basePath,
    params: { courseId, courseDetailsTab }
  },
  location: { pathname }
}) => {
  const { t } = useTranslation();
  const { canManageInstructors } = usePermissions();
  const [currentTab, setCurrentTab] = useState(0);
  const dispatch = useDispatch();

  const { courseData } = useSelector(({ contentCoursesState }) => ({
    courseData: selector(contentCoursesState, {
      type: "trainingObject",
      id: courseId
    }),
    loading: contentCoursesState.loading
  }));

  const tabs = [
    {
      title: t("manageCourses.instructors"),
      path: `${basePath}/instructors`
    },
    {
      title: t("manageCourses.locations"),
      path: `${basePath}/locations`
    },
    {
      title: t("manageCourses.virtualClassrooms"),
      path: `${basePath}/virtual-classrooms`
    }
  ];

  useEffect(() => {
    const currentPathIndex = tabs.findIndex(({ path }) => pathname === path);
    if (currentPathIndex !== -1) {
      setCurrentTab(currentPathIndex);
    } else {
      dispatch(push(tabs[0].path));
    }
  }, [pathname]);

  const handleTabChange = newTab => {
    setCurrentTab(newTab);
    dispatch(push(tabs[newTab].path));
  };

  const handleGoBack = () => {
    const scheduleSessionRoute = buildScheduleSessionRoute(courseId);
    if (!courseId) {
      dispatch(push(courses));
    } else if (matchPath(basePath, { path: scheduleSessionRoute })) {
      dispatch(push(scheduleSessionRoute));
    } else if (matchPath(basePath, { path: courseDetails })) {
      dispatch(push(buildCourseDetailsTabRoute(courseId, courseDetailsTab)));
    }
  };

  const courseName = courseData ? courseData.name : "";

  if (!canManageInstructors) {
    // Redirect user to admin root url if they do not have permission to manage instructors & locations
    dispatch(push(root));
  }

  return (
    <AdminPageLayout
      title={
        <Breadcrumb
          routes={[
            t("manageCourses.coursesPageAdminTitle"),
            courseName,
            t("manageCourses.ManangeInstrAndLocButton")
          ].filter(e => e !== "")}
        />
      }
      onGoBackClick={handleGoBack}
    >
      <>
        <Tabs
          tabs={tabs.map(({ title }) => title)}
          currentTab={currentTab}
          onChange={newTab => handleTabChange(newTab)}
          variant="scrollable"
          textVariant="button"
        />
        <Switch>
          <Route
            path={tabs[0].path}
            render={props => <Instructors {...props} />}
          />
          <Route path={tabs[1].path} render={() => <TrainingLocations />} />
          <Route
            path={tabs[2].path}
            render={() => <VirtualClassroomsTable />}
          />
        </Switch>
      </>
    </AdminPageLayout>
  );
};

ManageInstructorsLocations.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      /**
       * The course ID for the course
       */
      courseId: PropTypes.string
    }),
    path: PropTypes.string,
    url: PropTypes.string
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string
  })
};

export default withRouter(ManageInstructorsLocations);
